/**
 * Admin Dashboard Page for trAInedup.ai
 * Main administrative interface with analytics, user management, and system overview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  BookOpen,
  DollarSign,
  TrendingUp,
  MessageSquare,
  AlertTriangle,
  Settings,
  BarChart3,
  PieChart,
  Activity,
  Clock,
  FileText,
  Mail,
  Download,
  RefreshCw,
  Target,
  Ticket,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { adminService } from '../services/firebase/admin';
import { DashboardAnalytics, AnalyticsTimePeriod } from '../types/admin';
import { ROUTES, FEATURE_FLAGS } from '../utils/constants';

/**
 * Admin dashboard page component
 * Provides comprehensive overview of platform metrics and quick access to admin tools
 */
export const AdminDashboardPage: React.FC = () => {
  
  const navigate = useNavigate();
  
  // State
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<AnalyticsTimePeriod>(AnalyticsTimePeriod.LAST_30_DAYS);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load analytics data
   */
  const loadAnalytics = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const analyticsData = await adminService.getDashboardAnalytics(selectedPeriod);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setAnalytics(null);
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      // Optional: auto show sample analytics based on feature flag
      if (FEATURE_FLAGS.autoSampleAnalyticsOnError) {
        setAnalytics(buildSampleAnalytics(selectedPeriod));
      }
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  /**
   * Load dashboard analytics
   */
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics, selectedPeriod]);

  /**
   * Refresh analytics data
   */
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadAnalytics();
    } finally {
      setRefreshing(false);
    }
  };

  // Build sample analytics for demo/fallback
  const buildSampleAnalytics = (period: AnalyticsTimePeriod): DashboardAnalytics => {
    const endDate = new Date();
    const startDate = new Date();
    switch (period) {
      case AnalyticsTimePeriod.TODAY:
        startDate.setHours(0, 0, 0, 0);
        break;
      case AnalyticsTimePeriod.YESTERDAY:
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case AnalyticsTimePeriod.LAST_7_DAYS:
        startDate.setDate(startDate.getDate() - 7);
        break;
      case AnalyticsTimePeriod.LAST_30_DAYS:
        startDate.setDate(startDate.getDate() - 30);
        break;
      case AnalyticsTimePeriod.LAST_90_DAYS:
        startDate.setDate(startDate.getDate() - 90);
        break;
      case AnalyticsTimePeriod.LAST_YEAR:
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      default:
        startDate.setDate(startDate.getDate() - 30);
    }

    return {
      totalUsers: 2486,
      activeUsers: 735,
      newUsers: 112,
      userGrowthRate: 8.4,
      totalCourses: 128,
      publishedCourses: 94,
      totalEnrollments: 1735,
      completionRate: 42.7,
      totalRevenue: 125000,
      monthlyRecurringRevenue: 15000,
      averageRevenuePerUser: 89.5,
      revenueGrowthRate: 12.5,
      totalPosts: 562,
      totalMessages: 2143,
      averageSessionDuration: 1800,
      dailyActiveUsers: 320,
      totalTickets: 45,
      openTickets: 12,
      averageResponseTime: 4.2,
      customerSatisfactionScore: 4.6,
      period,
      startDate,
      endDate,
      lastUpdated: new Date(),
    };
  };

  const loadSampleData = () => {
    setError(null);
    setAnalytics(buildSampleAnalytics(selectedPeriod));
  };

  /**
   * Format number with commas
   */
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  /**
   * Format percentage
   */
  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  /**
   * Get period display name
   */
  const getPeriodDisplayName = (period: AnalyticsTimePeriod): string => {
    switch (period) {
      case AnalyticsTimePeriod.TODAY:
        return 'Today';
      case AnalyticsTimePeriod.YESTERDAY:
        return 'Yesterday';
      case AnalyticsTimePeriod.LAST_7_DAYS:
        return 'Last 7 Days';
      case AnalyticsTimePeriod.LAST_30_DAYS:
        return 'Last 30 Days';
      case AnalyticsTimePeriod.LAST_90_DAYS:
        return 'Last 90 Days';
      case AnalyticsTimePeriod.LAST_YEAR:
        return 'Last Year';
      default:
        return 'Last 30 Days';
    }
  };

  /**
   * Metric card component
   */
  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    change?: number;
    icon: React.ComponentType<any>;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, change, icon: Icon, color, onClick }) => (
    <Card 
      className={`p-6 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-neutral-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-neutral-900">{value}</p>
          {change !== undefined && (
            <p className={`text-sm mt-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercentage(change)} from last period
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </Card>
  );

  /**
   * Quick action card component
   */
  const QuickActionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ComponentType<any>;
    color: string;
    onClick: () => void;
  }> = ({ title, description, icon: Icon, color, onClick }) => (
    <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={onClick}>
      <div className="flex items-start space-x-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-neutral-900 mb-1">{title}</h3>
          <p className="text-sm text-neutral-600">{description}</p>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );
  }
 
  return (
      <div className="py-8">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           {/* Page Header */}
           <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
             <div>
               <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-2">
                 Admin Dashboard
               </h1>
               <p className="text-lg text-neutral-600">
                 Platform overview and administrative controls
               </p>
             </div>
             
             <div className="mt-6 lg:mt-0 flex items-center space-x-4">
               {/* Period selector */}
               <select
                 value={selectedPeriod}
                 onChange={(e) => setSelectedPeriod(e.target.value as AnalyticsTimePeriod)}
                 className="border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
               >
                 {Object.values(AnalyticsTimePeriod).map(period => (
                   <option key={period} value={period}>
                     {getPeriodDisplayName(period)}
                   </option>
                 ))}
               </select>
               
               <Button
                 variant="outline"
                 icon={RefreshCw}
                 onClick={handleRefresh}
                 loading={refreshing}
               >
                 Refresh
               </Button>
               
               <Button
                 variant="outline"
                 icon={DollarSign}
                 onClick={() => navigate(ROUTES.adminAffiliates)}
               >
                 Manage Affiliates
               </Button>

               <Button
                 variant="outline"
                 icon={Download}
               >
                 Export
               </Button>
             </div>
           </div>

           {FEATURE_FLAGS.showAdminSeedButton && (
             <div className="mb-6 flex items-center gap-3">
               <Button
                 variant="outline"
                 onClick={async () => {
                   try {
                     await adminService.seedSampleData();
                     await handleRefresh();
                   } catch (e) {
                     console.error('Seeding failed', e);
                   }
                 }}
               >
                 Seed Sample Data
               </Button>
               {error && (
                 <span className="text-sm text-red-600">
                   {error}
                 </span>
               )}
             </div>
           )}

           {analytics ? (
             <>
               {/* Key Metrics */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                 <MetricCard
                   title="Total Users"
                   value={formatNumber(analytics.totalUsers)}
                   change={analytics.userGrowthRate}
                   icon={Users}
                   color="bg-blue-500"
                  onClick={() => navigate(ROUTES.adminUsers)}
                 />
                 
                 <MetricCard
                   title="Active Courses"
                   value={formatNumber(analytics.publishedCourses)}
                   icon={BookOpen}
                   color="bg-green-500"
                  onClick={() => navigate(ROUTES.adminCourses)}
                 />
                 
                 <MetricCard
                   title="Monthly Revenue"
                   value={formatCurrency(analytics.monthlyRecurringRevenue)}
                   change={analytics.revenueGrowthRate}
                   icon={DollarSign}
                   color="bg-purple-500"
                  onClick={() => navigate(ROUTES.adminAnalytics)}
                 />
                 
                 <MetricCard
                   title="Course Completion"
                   value={`${analytics.completionRate.toFixed(1)}%`}
                   icon={TrendingUp}
                   color="bg-orange-500"
                 />
               </div>

               {/* Secondary Metrics */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                 <MetricCard
                   title="Daily Active Users"
                   value={formatNumber(analytics.dailyActiveUsers)}
                   icon={Activity}
                   color="bg-teal-500"
                 />
                 
                 <MetricCard
                   title="Community Posts"
                   value={formatNumber(analytics.totalPosts)}
                   icon={MessageSquare}
                   color="bg-indigo-500"
                 />
                 
                 <MetricCard
                   title="Support Tickets"
                   value={`${analytics.openTickets}/${analytics.totalTickets}`}
                   icon={AlertTriangle}
                   color="bg-red-500"
                 />
                 
                 <MetricCard
                   title="Avg Response Time"
                   value={`${analytics.averageResponseTime.toFixed(1)}h`}
                   icon={Clock}
                   color="bg-yellow-500"
                 />
               </div>

               {/* Charts Section */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                 {/* User Growth Chart */}
                 <Card className="p-6">
                   <div className="flex items-center justify-between mb-6">
                     <h3 className="font-semibold text-lg text-neutral-800">User Growth</h3>
                     <Button variant="ghost" size="sm" icon={BarChart3} />
                   </div>
                   
                   <div className="h-64 flex items-center justify-center bg-neutral-50 rounded-lg">
                     <div className="text-center">
                       <BarChart3 className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                       <p className="text-neutral-600">Chart visualization would go here</p>
                       <p className="text-sm text-neutral-500">Integration with charting library needed</p>
                     </div>
                   </div>
                 </Card>

                 {/* Revenue Chart */}
                 <Card className="p-6">
                   <div className="flex items-center justify-between mb-6">
                     <h3 className="font-semibold text-lg text-neutral-800">Revenue Trends</h3>
                     <Button variant="ghost" size="sm" icon={PieChart} />
                   </div>
                   
                   <div className="h-64 flex items-center justify-center bg-neutral-50 rounded-lg">
                     <div className="text-center">
                       <PieChart className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                       <p className="text-neutral-600">Revenue chart would go here</p>
                       <p className="text-sm text-neutral-500">Integration with charting library needed</p>
                     </div>
                   </div>
                 </Card>
               </div>

               {/* Quick Actions */}
               <div className="mb-8">
                 <h2 className="font-semibold text-xl text-neutral-800 mb-6">Quick Actions</h2>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <QuickActionCard
                     title="Manage Users"
                     description="View, edit, and moderate user accounts"
                     icon={Users}
                     color="bg-blue-500"
                    onClick={() => navigate(ROUTES.adminUsers)}
                   />

                   <QuickActionCard
                     title="Affiliate Program"
                     description="Approve and manage affiliate partners"
                     icon={DollarSign}
                     color="bg-indigo-500"
                     onClick={() => navigate(ROUTES.adminAffiliates)}
                   />
                   
                   <QuickActionCard
                     title="Assessment Review"
                     description="Review user skills assessments and results"
                     icon={Target}
                     color="bg-amber-500"
                     onClick={() => navigate('/dashboard/admin/assessments')}
                   />
                   
                   <QuickActionCard
                     title="Manage Courses"
                     description="Create, edit, and publish courses"
                     icon={BookOpen}
                     color="bg-green-500"
                     onClick={() => navigate(ROUTES.adminCourses)}
                   />
                   
                   <QuickActionCard
                     title="Email Management"
                     description="View email history and send campaigns"
                     icon={Mail}
                     color="bg-pink-500"
                     onClick={() => navigate(ROUTES.adminEmails)}
                   />
                   
                   <QuickActionCard
                     title="Content Management"
                     description="Review and publish course content"
                     icon={FileText}
                     color="bg-emerald-500"
                    onClick={() => navigate(ROUTES.adminCourses)}
                   />
                   
                   <QuickActionCard
                     title="System Settings"
                     description="Configure platform settings and features"
                     icon={Settings}
                     color="bg-purple-500"
                    onClick={() => navigate(ROUTES.adminSettings)}
                   />
                   
                   <QuickActionCard
                     title="Support Tickets"
                     description="Manage customer support requests"
                     icon={Ticket}
                     color="bg-red-500"
                     onClick={() => navigate(ROUTES.adminSupport)}
                   />
                   
                   <QuickActionCard
                     title="Analytics Reports"
                     description="Generate detailed analytics reports"
                     icon={BarChart3}
                     color="bg-teal-500"
                     onClick={() => navigate(ROUTES.adminAnalytics)}
                   />
                 </div>
               </div>

               {/* Recent Activity */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 {/* Recent Users */}
                 <Card className="p-6">
                   <div className="flex items-center justify-between mb-6">
                     <h3 className="font-semibold text-lg text-neutral-800">Recent Users</h3>
                     <Link
                       to={ROUTES.adminUsers}
                       className="text-primary hover:text-primary-dark text-sm font-medium"
                     >
                       View all
                     </Link>
                   </div>
                   
                   <div className="space-y-4">
                     {[1, 2, 3, 4, 5].map(i => (
                       <div key={i} className="flex items-center space-x-3">
                         <div className="w-10 h-10 bg-neutral-200 rounded-full flex items-center justify-center">
                           <span className="text-sm font-medium text-neutral-600">U{i}</span>
                         </div>
                         <div className="flex-1">
                           <p className="font-medium text-neutral-800">User {i}</p>
                           <p className="text-sm text-neutral-600">user{i}@example.com</p>
                         </div>
                         <div className="text-right">
                           <p className="text-sm text-neutral-600">2 hours ago</p>
                           <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                         </div>
                       </div>
                     ))}
                   </div>
                 </Card>

                 {/* System Health */}
                 <Card className="p-6">
                   <div className="flex items-center justify-between mb-6">
                     <h3 className="font-semibold text-lg text-neutral-800">System Health</h3>
                     <div className="flex items-center space-x-2">
                       <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                       <span className="text-sm text-green-600 font-medium">All Systems Operational</span>
                     </div>
                   </div>
                   
                   <div className="space-y-4">
                     <div className="flex items-center justify-between">
                       <span className="text-neutral-600">API Response Time</span>
                       <span className="font-medium text-neutral-800">245ms</span>
                     </div>
                     
                     <div className="flex items-center justify-between">
                       <span className="text-neutral-600">Database Performance</span>
                       <span className="font-medium text-green-600">Excellent</span>
                     </div>
                     
                     <div className="flex items-center justify-between">
                       <span className="text-neutral-600">CDN Status</span>
                       <span className="font-medium text-green-600">Online</span>
                     </div>
                     
                     <div className="flex items-center justify-between">
                       <span className="text-neutral-600">Error Rate</span>
                       <span className="font-medium text-neutral-800">0.02%</span>
                     </div>
                     
                     <div className="flex items-center justify-between">
                       <span className="text-neutral-600">Uptime</span>
                       <span className="font-medium text-green-600">99.98%</span>
                     </div>
                   </div>
                 </Card>
               </div>
             </>
           ) : (
             <Card className="p-8 text-center bg-neutral-50">
               <h2 className="text-xl font-semibold text-neutral-800 mb-2">Analytics unavailable</h2>
               <p className="text-neutral-600 max-w-xl mx-auto">
                 {error ? `We couldn't load analytics right now (${error}).` : 'No analytics data available yet.'}
               </p>
               <div className="mt-6 flex items-center justify-center gap-3">
                 <Button variant="outline" icon={RefreshCw} onClick={handleRefresh} loading={refreshing}>
                   Retry
                 </Button>
                 <Button icon={BarChart3} onClick={loadSampleData}>
                   Load Sample Data
                 </Button>
               </div>
             </Card>
           )}
         </div>
       </div>
  );
 }
